'use client'

import React from 'react'

import { Box, chakra } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import { ChevronDownIcon, ChevronUpIcon } from '../Icon'
import {
  fieldLabelStyles,
  fieldLabelWrapperStyles,
} from '../shared/fieldLabelStyles'
import {
  numberInputContainerStyles,
  numberInputFieldStyles,
  numberInputStepperButtonStyles,
  numberInputStepperStyles,
  numberInputWarningStyles,
} from './NumberInput.styles'

type CommonNumberInputProps = {
  label?: string
  id?: string
  colorScheme?: ColorScheme
  width?: string | number
  min?: number
  max?: number
  step?: number
  disabled?: boolean
  hasWarning?: boolean
  warningText?: string
  'data-testid'?: string
}

type BoundedNumberInputProps = {
  /**
   * The default mode commits finite numbers and clamps them to min/max.
   * Existing consumers use this for counters and other always-valid values.
   */
  editMode?: 'bounded'
  value: number
  onValueChange: (value: number) => void
}

type RawNumberInputProps = {
  /**
   * Raw mode preserves in-progress and invalid text so form owners can show
   * validation instead of silently replacing the user's input.
   */
  editMode: 'raw'
  value: string
  onValueChange: (value: string) => void
}

export type NumberInputProps = CommonNumberInputProps &
  (BoundedNumberInputProps | RawNumberInputProps)

const clamp = (value: number, min?: number, max?: number) =>
  Math.min(max ?? Number.POSITIVE_INFINITY, Math.max(min ?? 0, value))

const decimalPrecision = (value: number) => {
  const [coefficient, exponentText = '0'] = value
    .toString()
    .toLowerCase()
    .split('e')
  const decimalPlaces = coefficient.split('.')[1]?.length ?? 0

  return Math.max(0, decimalPlaces - Number(exponentText))
}

// Add in integer space so decimal steps do not accumulate binary floating
// point artifacts, while preserving an existing off-step decimal value.
const addStep = (value: number, step: number) => {
  const factor = 10 ** Math.max(decimalPrecision(value), decimalPrecision(step))

  return (Math.round(value * factor) + Math.round(step * factor)) / factor
}

const parseNumberInputValue = (value: number | string): number => {
  if (typeof value === 'number') return value

  return value.trim() === '' ? Number.NaN : Number(value)
}

export const NumberInput = (props: NumberInputProps) => {
  const {
    value,
    label,
    id,
    colorScheme = 'dark',
    width = '200px',
    min = 0,
    max,
    step = 1,
    disabled: isDisabled = false,
    hasWarning = false,
    warningText,
    'data-testid': dataTestId,
  } = props
  const generatedId = React.useId()
  const inputId = id ?? dataTestId ?? generatedId
  const warningId = `${inputId}-warning`
  const accessibleName = label ?? 'value'
  const isRawEditMode = props.editMode === 'raw'
  const parsedValue = parseNumberInputValue(value)
  const isDecrementDisabled =
    isDisabled || (Number.isFinite(parsedValue) && parsedValue <= min)
  const isIncrementDisabled =
    isDisabled ||
    (Number.isFinite(parsedValue) && max !== undefined && parsedValue >= max)

  const updateValue = (nextValue: number) => {
    if (!isDisabled && Number.isFinite(nextValue)) {
      const nextBoundedValue = clamp(nextValue, min, max)
      if (props.editMode === 'raw') {
        props.onValueChange(String(nextBoundedValue))
      } else {
        props.onValueChange(nextBoundedValue)
      }
    }
  }

  const stepValue = (direction: 1 | -1) => {
    const currentValue = Number.isFinite(parsedValue) ? parsedValue : min
    updateValue(addStep(currentValue, direction * step))
  }

  const control = (
    <Box width={label ? '100%' : width}>
      <Box
        {...numberInputContainerStyles(
          colorScheme,
          isDisabled,
          '100%',
          hasWarning
        )}
      >
        <chakra.input
          aria-describedby={hasWarning && warningText ? warningId : undefined}
          aria-invalid={hasWarning || undefined}
          aria-label={label ? undefined : accessibleName}
          data-testid={dataTestId}
          disabled={isDisabled}
          id={inputId}
          inputMode={isRawEditMode ? 'decimal' : undefined}
          max={isRawEditMode ? undefined : max}
          min={isRawEditMode ? undefined : min}
          onChange={(event) => {
            if (isRawEditMode) {
              props.onValueChange(event.target.value)
              return
            }

            // Number inputs report NaN for in-progress entries ("10." en
            // route to "10.5", or a cleared field). Bounded mode keeps its
            // last committed value; raw mode above preserves the text.
            const nextValue = event.target.valueAsNumber
            if (Number.isNaN(nextValue)) {
              return
            }
            updateValue(nextValue)
          }}
          onKeyDown={(event) => {
            if (
              isRawEditMode &&
              (event.key === 'ArrowUp' || event.key === 'ArrowDown')
            ) {
              event.preventDefault()
              stepValue(event.key === 'ArrowUp' ? 1 : -1)
            }
          }}
          step={isRawEditMode ? undefined : step}
          type={isRawEditMode ? 'text' : 'number'}
          value={value}
          {...numberInputFieldStyles(colorScheme, isDisabled)}
        />
        <Box {...numberInputStepperStyles(colorScheme)}>
          <chakra.button
            aria-label={`Increase ${accessibleName}`}
            disabled={isIncrementDisabled}
            onClick={() => stepValue(1)}
            type='button'
            {...numberInputStepperButtonStyles(colorScheme)}
            borderBottom='1px solid'
            borderBottomColor={
              colorScheme === 'light' ? 'light.border.search' : 'border.search'
            }
          >
            <ChevronUpIcon height={10} width={10} />
          </chakra.button>
          <chakra.button
            aria-label={`Decrease ${accessibleName}`}
            disabled={isDecrementDisabled}
            onClick={() => stepValue(-1)}
            type='button'
            {...numberInputStepperButtonStyles(colorScheme)}
          >
            <ChevronDownIcon height={10} width={10} />
          </chakra.button>
        </Box>
      </Box>
      {hasWarning && warningText && (
        <Box
          id={warningId}
          role='alert'
          {...numberInputWarningStyles(colorScheme)}
        >
          {warningText}
        </Box>
      )}
    </Box>
  )

  if (!label) return control

  return (
    <Box {...fieldLabelWrapperStyles(width)}>
      <chakra.label htmlFor={inputId} {...fieldLabelStyles(colorScheme)}>
        {label}
      </chakra.label>
      {control}
    </Box>
  )
}
